<template>
  <div v-if="showCard" class="mt-3 bg-white rounded-lg border overflow-hidden" :class="cardShadowClasses">
    <div class="px-5 py-5 sm:px-6 sm:py-6">
      <!-- Disrupted delivery: the tracker is replaced by a single calm notice -->
      <div v-if="isDisrupted" class="flex items-start gap-3 rounded-md bg-red-50 border border-red-200 px-4 py-3">
        <ExclamationCircleIcon class="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <p class="text-sm text-red-700">This delivery could not be completed. The store will be in touch.</p>
      </div>

      <template v-else>
        <ol class="flex items-start" aria-label="Delivery progress">
          <li
            v-for="(label, index) in stepLabels"
            :key="label"
            class="relative flex-1 flex flex-col items-center text-center"
          >
            <div
              v-if="index > 0"
              aria-hidden="true"
              class="absolute top-3 right-1/2 w-full h-0.5"
              :class="connectorClass(index)"
            ></div>
            <span
              class="relative z-10 flex h-6 w-6 items-center justify-center rounded-full border-2"
              :class="circleClass(index)"
            >
              <CheckIcon v-if="isStepComplete(index)" class="h-3.5 w-3.5 text-white" aria-hidden="true" />
              <span v-else-if="isStepCurrent(index)" class="relative flex h-2 w-2">
                <span
                  class="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-600 opacity-75"
                  aria-hidden="true"
                ></span>
                <span class="relative inline-flex h-2 w-2 rounded-full bg-brand-600"></span>
              </span>
            </span>
            <span
              class="mt-2 text-xs font-semibold leading-tight"
              :class="isStepUpcoming(index) ? 'text-gray-brand-500' : 'text-gray-brand-900'"
              :aria-current="isStepCurrent(index) ? 'step' : null"
            >
              {{ label }}
            </span>
            <span v-if="showsArrivingSoon(index)" class="text-[11px] text-brand-600 mt-0.5">Arriving soon</span>
          </li>
        </ol>

        <div class="mt-5">
          <p class="text-lg font-extrabold text-gray-brand-900 tracking-tight">{{ headline }}</p>
          <p v-if="subtext" class="mt-0.5 text-sm text-gray-brand-600">{{ subtext }}</p>
        </div>

        <div
          v-if="addressLine || courierLabel"
          class="mt-4 pt-4 border-t border-gray-brand-200 text-sm text-gray-brand-700 space-y-1"
        >
          <div v-if="addressLine">{{ addressLine }}</div>
          <div v-if="courierLabel">{{ courierLabel }}</div>
        </div>

        <a
          v-if="showTrackButton"
          :href="delivery.tracking_url"
          target="_blank"
          rel="noopener"
          class="mt-5 flex items-center justify-center w-full rounded-lg border border-brand-600 bg-brand-500 hover:bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white pointer-events-auto"
        >
          Track your delivery
        </a>
        <a
          v-else-if="showTrackLink"
          :href="delivery.tracking_url"
          target="_blank"
          rel="noopener"
          class="mt-5 inline-block text-sm font-semibold text-brand-600 hover:text-brand-700 underline pointer-events-auto"
        >
          View delivery details
        </a>

        <p v-if="showFooterNote" class="mt-4 text-xs text-gray-brand-500">
          We will text you when your order is on its way.
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import { CheckIcon, ExclamationCircleIcon } from '@heroicons/vue/solid'
import { cardShadowClasses } from '@/config'
import {
  DELIVERY_STEP_LABELS,
  deliveryStep,
  deliveryHeadline,
  isTerminalDeliveryStatus,
  showsCourierName,
  courierFirstName
} from '@/utils/deliveryStatus'

const DISRUPTED_STATUSES = ['canceled', 'returned']
const MOVING_STATUSES = ['pickup', 'pickup_complete', 'dropoff']

export default {
  name: 'DeliveryStatus',
  components: { CheckIcon, ExclamationCircleIcon },
  props: {
    delivery: {
      type: Object,
      default: null
    },
    merchantTimeZone: {
      type: String,
      default: ''
    },
    address: {
      type: Object,
      default: null
    }
  },
  data: () => ({ cardShadowClasses, stepLabels: DELIVERY_STEP_LABELS }),
  computed: {
    status() {
      return this.delivery?.status
    },
    showCard() {
      return Boolean(this.delivery) && this.status !== 'failed'
    },
    isDisrupted() {
      return DISRUPTED_STATUSES.includes(this.status)
    },
    currentStep() {
      return deliveryStep(this.status)
    },
    headline() {
      return deliveryHeadline(this.status)
    },
    subtext() {
      if (this.status === 'pending') return this.bookedSubtext
      if (MOVING_STATUSES.includes(this.status)) return this.etaSubtext
      if (this.status === 'delivered') return this.deliveredSubtext
      return ''
    },
    bookedSubtext() {
      const start = this.delivery?.pickup_ready_at
      const end = this.delivery?.dropoff_deadline_at
      if (!start) return ''
      const dayWord = this.dayWordFor(start)
      if (end) return `Arriving ${dayWord} between ${this.formatTime(start)} and ${this.formatTime(end)}`
      return `Arriving ${dayWord} from ${this.formatTime(start)}`
    },
    etaSubtext() {
      const eta = this.delivery?.dropoff_eta
      return eta ? `Estimated arrival ${this.formatTime(eta)}` : ''
    },
    deliveredSubtext() {
      const deliveredAt = this.delivery?.delivered_at
      return deliveredAt ? `Delivered at ${this.formatTime(deliveredAt)}` : ''
    },
    addressLine() {
      if (!this.address) return ''
      return [this.address.address1, this.address.address2, this.address.city, this.address.state, this.address.zip]
        .filter(Boolean)
        .join(', ')
    },
    courierLabel() {
      const showCourier = Boolean(this.delivery?.courier_name) && showsCourierName(this.status)
      return showCourier ? `Courier: ${courierFirstName(this.delivery.courier_name)}` : ''
    },
    showTrackButton() {
      return Boolean(this.delivery?.tracking_url) && !isTerminalDeliveryStatus(this.status)
    },
    showTrackLink() {
      return Boolean(this.delivery?.tracking_url) && this.status === 'delivered'
    },
    showFooterNote() {
      return this.status === 'pending' || this.status === 'pickup'
    }
  },
  methods: {
    isStepComplete(index) {
      if (this.status === 'delivered') return true
      return index + 1 < this.currentStep
    },
    isStepCurrent(index) {
      return this.status !== 'delivered' && index + 1 === this.currentStep
    },
    isStepUpcoming(index) {
      return !this.isStepComplete(index) && !this.isStepCurrent(index)
    },
    circleClass(index) {
      if (this.isStepComplete(index)) return 'bg-brand-600 border-brand-600'
      if (this.isStepCurrent(index)) return 'bg-white border-brand-600'
      return 'bg-white border-gray-brand-300'
    },
    connectorClass(index) {
      return this.isStepComplete(index - 1) ? 'bg-brand-600' : 'bg-gray-brand-300'
    },
    showsArrivingSoon(index) {
      return index === 2 && this.status === 'dropoff'
    },
    formatTime(iso) {
      if (!iso) return ''
      return new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hourCycle: 'h12',
        timeZone: this.merchantTimeZone || undefined
      }).format(new Date(iso))
    },
    dayWordFor(iso) {
      const target = new Date(iso)
      const now = new Date()
      const tomorrow = new Date(now)
      tomorrow.setDate(tomorrow.getDate() + 1)
      const dateKey = (date) =>
        new Intl.DateTimeFormat('en-CA', {
          timeZone: this.merchantTimeZone || undefined,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).format(date)
      if (dateKey(target) === dateKey(now)) return 'today'
      if (dateKey(target) === dateKey(tomorrow)) return 'tomorrow'
      return new Intl.DateTimeFormat('en-US', {
        timeZone: this.merchantTimeZone || undefined,
        weekday: 'long',
        month: 'short',
        day: 'numeric'
      }).format(target)
    }
  }
}
</script>
